import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Row, Col, CardGroup, Card, CardBody, FormGroup, Label, Input, InputGroup } from 'reactstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import PhoneInput from 'react-phone-number-input';
import { ArrowLinkButton, CustomButton } from '../../../containers';
import { ScreenTitleContainer } from '../../../containers';
import {
  ROUTE_TO_ADMIN_TRANSACTIONS_PAGE,
  ROUTE_TO_ADMIN_EDIT_USER_PAGE,
  ROUTE_TO_ADMIN_EDIT_EMPLOYEE_PAGE,
} from '../../../constants/routes';
import { displayCurrencyOptions } from '../../../utils/currencyDisplayFormat';
import { formatDateTime } from '../../../constants/dateFormatting';
import { InvoiceInfoSection } from './InvoiceInfoSection';
import { PolicyInfoSection } from './PolicyInfoSection';
import { TRANSACTION_NEW_POLICY, TRANSACTION_STATUS_PAID } from '../../../constants/userTransactions';
import { USER_ACCOUNT_BUSINESS } from '../../../constants/userDefs';
import { isHOKEmployee } from '../../../constants/roles';
import { TransactionManagement } from './TransactionManagement';
import { currencyConversionEuro } from '../../../utils/currencyConversion';
import { currencyDisplayUpdate } from '../../../components/CurrencyDisplay';
import { useSelector } from 'react-redux';

export const SingleTransactionPreview = React.memo(({ transaction, user, fetchMore }) => {
  const history = useHistory();
  const { formatMessage, formatNumber } = useIntl();

  const payload = useMemo(() => {
    try {
      const transactionPayload = JSON.parse(transaction.payload);
      return transactionPayload || {};
    } catch (e) {
      return {};
    }
  }, [transaction.payload]);
  const documents = useMemo(() => {
    try {
      const docs = JSON.parse(transaction.documents);
      return Array.isArray(docs) ? docs : [];
    } catch (e) {
      return [];
    }
  }, [transaction.documents]);

  const fullName =
    user.sector === USER_ACCOUNT_BUSINESS
      ? user.businessName
      : `${user.firstName || ''} ${user.lastName || ''}`.trim() || '';

  const redirectToUserProfile = useCallback(() => {
    if (user.id) {
      const isEmployee = isHOKEmployee(user);
      const route = isEmployee ? ROUTE_TO_ADMIN_EDIT_EMPLOYEE_PAGE : ROUTE_TO_ADMIN_EDIT_USER_PAGE;
      history.push(`${route}/${user.id}`);
    }
  }, [history, user]);

  const openWSPayTransaction = () => {
    if (transaction.transactionReferenceId) {
      window.open(`https://administration.wspay.biz/HR/Transaction/Edit/${transaction.transactionReferenceId}`);
    }
  };

  const currencyDataConfig = useSelector(state => state.currencyConfig);

  currencyDisplayUpdate(currencyDataConfig.defaultCurrency);

  const formatOrderAmountDisplayPrice  = (totalAmount, dualCurrencyDisplay) => {
    if(dualCurrencyDisplay === true){
      return formatNumber(
        totalAmount / 100,
        displayCurrencyOptions
        )
        + " | " +
        currencyConversionEuro(totalAmount)
    }else{
      return formatNumber(
        totalAmount / 100,
        displayCurrencyOptions
      )
    }
  }

  return (
    <Container className="mt-4" fluid>
      <ArrowLinkButton xlColSize="10" linkTo={ROUTE_TO_ADMIN_TRANSACTIONS_PAGE} />
      <ScreenTitleContainer
        className="mt-4"
        xlColSize="10"
        xlColOffset="1"
        row
        raw
        titleId={
          <>
            <FormattedMessage id="adminUserTransactionsPage.order" /> #{transaction.confirmationCode}
            {transaction.transactionReference && (
              <>
                : <div className="d-inline-block text-dark">"{transaction.transactionReference}"</div>
              </>
            )}
          </>
        }
      />
      <Row>
        <Col xs={12} xl={{ size: 10, offset: 1 }}>
          <CardGroup>
            <Card>
              <CardBody>
                <Row className="my-4">
                  <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                    <FormGroup>
                      <h3 className="mb-4">
                        <FormattedMessage id="adminUserTransactionsPage.orderInfo" />
                      </h3>
                      <Label for="orderId">
                        <FormattedMessage id="inputs.orderId" />
                      </Label>
                      <InputGroup>
                        <Input id="orderId" readOnly defaultValue={transaction.confirmationCode} />
                      </InputGroup>
                      <Label for="transactionId">
                        <FormattedMessage id="inputs.transactionId" />
                      </Label>
                      <InputGroup>
                        <Input id="transactionId" readOnly defaultValue={transaction.transactionReference} />
                      </InputGroup>
                      <Label for="orderType">
                        <FormattedMessage id="inputs.orderType" />
                      </Label>
                      <InputGroup>
                        <Input
                          className="text-uppercase"
                          id="orderType"
                          readOnly
                          defaultValue={formatMessage({
                            id: `adminUserTransactionsPage.orderTypeFilter.${transaction.type}`,
                          })}
                        />
                      </InputGroup>
                      <Label for="orderDateTime">
                        <FormattedMessage id="inputs.orderDateTime" />
                      </Label>
                      <InputGroup>
                        <Input
                          id="orderDateTime"
                          readOnly
                          defaultValue={formatDateTime(transaction.createdAt, false)}
                        />
                      </InputGroup>
                      <Label for="orderAmount">
                        <FormattedMessage id="inputs.orderAmount" />
                      </Label>
                      <InputGroup>
                        <Input
                          className="font-weight-bold"
                          id="orderAmount"
                          readOnly
                          defaultValue=
                          {formatOrderAmountDisplayPrice(transaction.totalAmount, currencyDataConfig.dualCurrencyDisplay)}
                          /* {`${formatNumber(
                            transaction.totalAmount / 100,
                            displayCurrencyOptions
                          )} | ${currencyConversionEuro(transaction.totalAmount)}`} */
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col xs={{ size: 12, order: 1 }} md="6">
                    <TransactionManagement transaction={transaction} fetchMore={fetchMore} />
                  </Col>
                </Row>
                <Row className="no-gutters my-4">
                  <Col xs="12">
                    <Row className="no-gutters">
                      <Col xs="12">
                        <h3 className="mb-4">
                          <FormattedMessage id="adminUserTransactionsPage.userInfo" />
                        </h3>
                      </Col>
                    </Row>
                    <FormGroup row>
                      <Col xs="12" sm="6">
                        <Label for="fullName">
                          <FormattedMessage id="inputs.fullName" />
                        </Label>
                        <InputGroup>
                          <Input id="fullName" readOnly defaultValue={fullName} />
                        </InputGroup>
                      </Col>
                      <Col xs="12" sm="6">
                        <Label for="sector">
                          <FormattedMessage id="inputs.sector" />
                        </Label>
                        <InputGroup>
                          <Input
                            className="text-uppercase"
                            id="sector"
                            readOnly
                            defaultValue={user.sector ? formatMessage({ id: `accountType.${user.sector}` }) : ''}
                          />
                        </InputGroup>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs="12" xl="6">
                        <FormGroup row>
                          <Col xs="12" sm="6" xl="12">
                            <Label for="userId">
                              <FormattedMessage id="inputs.userId" />
                            </Label>
                            <InputGroup>
                              <Input
                                className="hok-standard-input"
                                id="userId"
                                readOnly
                                defaultValue={transaction.userId}
                              />
                              <CustomButton
                                className="hok-dashboard-btn d-none d-xl-inline-block ml-0 ml-sm-3"
                                block={false}
                                translationId="openProfile"
                                disabled={!user.id}
                                onClick={redirectToUserProfile}
                              />
                            </InputGroup>
                          </Col>
                          <Col xs="12" sm="6" className="d-flex align-items-end d-xl-none">
                            <CustomButton
                              className="hok-dashboard-btn d-block w-xs-100 d-sm-inline-block d-xl-none mb-0 mb-sm-3"
                              block={false}
                              translationId="openProfile"
                              disabled={!user.id}
                              onClick={redirectToUserProfile}
                            />
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="6">
                        <Label for="partnerCode">
                          <FormattedMessage id="inputs.partnerCode" />
                        </Label>
                        <InputGroup>
                          <Input id="partnerCode" readOnly defaultValue={user.partnerId} />
                        </InputGroup>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col xs="12" sm="6">
                        <Label for="telNumber">
                          <FormattedMessage id="inputs.telNumber" />
                        </Label>
                        <InputGroup>
                          <Input
                            id="telNumber"
                            className="disabled"
                            tag={PhoneInput}
                            defaultCountry="HR"
                            disabled
                            type="tel"
                            addInternationalOption={false}
                            value={user.phoneNumber}
                            onChange={() => null}
                          />
                        </InputGroup>
                      </Col>
                      <Col xs="12" sm="6">
                        <Label for="email">
                          <FormattedMessage id="inputs.email" />
                        </Label>
                        <InputGroup>
                          <Input id="email" readOnly defaultValue={user.email} />
                        </InputGroup>
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
                {transaction.type === TRANSACTION_NEW_POLICY ? (
                  <PolicyInfoSection
                    confirmationCode={transaction.confirmationCode}
                    referenceNumber={transaction.referenceNumber}
                    payload={payload}
                    documents={documents}
                  />
                ) : (
                  <InvoiceInfoSection
                    confirmationCode={transaction.confirmationCode}
                    referenceNumber={transaction.referenceNumber}
                  />
                )}
                {transaction.status === TRANSACTION_STATUS_PAID && (
                  <Row className="no-gutters my-4">
                    <Col xs="12">
                      <Row className="no-gutters">
                        <Col xs="12">
                          <h3 className="mb-4">
                            <FormattedMessage id="adminUserTransactionsPage.payerInfo" />
                          </h3>
                        </Col>
                      </Row>
                      <FormGroup row>
                        <Col xs="12" sm="6">
                          <Label for="payerFullName">
                            <FormattedMessage id="inputs.fullName" />
                          </Label>
                          <InputGroup>
                            <Input id="payerFullName" readOnly defaultValue={transaction.userName} />
                          </InputGroup>
                        </Col>
                        <Col xs="12" xl="6">
                          <Row>
                            <Col xs="12" sm="6" xl="12">
                              <Label for="paymentMethod">
                                <FormattedMessage id="inputs.paymentMethod" />
                              </Label>
                              <InputGroup>
                                <Input
                                  className="hok-standard-input"
                                  id="paymentMethod"
                                  readOnly
                                  defaultValue={transaction.paymentProcessor}
                                />
                                {transaction.transactionReferenceId && (
                                  <CustomButton
                                    className="hok-dashboard-btn d-none d-xl-inline-block ml-0 ml-sm-3"
                                    block={false}
                                    translationId="openTransaction"
                                    onClick={openWSPayTransaction}
                                  />
                                )}
                              </InputGroup>
                            </Col>
                            {transaction.transactionReferenceId && (
                              <Col xs="12" sm="6" className="d-flex align-items-end d-xl-none">
                                <CustomButton
                                  className="hok-dashboard-btn d-block w-xs-100 d-sm-inline-block d-xl-none mb-0 mb-sm-3"
                                  block={false}
                                  translationId="openTransaction"
                                  onClick={openWSPayTransaction}
                                />
                              </Col>
                            )}
                          </Row>
                        </Col>
                      </FormGroup>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </CardGroup>
        </Col>
      </Row>
    </Container>
  );
});
